import React, { useEffect, useState, useCallback } from 'react';
import { toast } from 'sonner';
import { axiosInstance as axios } from '../../../context/AuthContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../ui/dialog';
import { STATUS_LABELS } from './OversightTab';

const ACTIONS = {
  pending: ['approve', 'request_info', 'reject'],
  info_requested: ['approve', 'request_info', 'reject'],
  approved: ['suspend'],
  suspended: ['reinstate', 'reject'],
  rejected: ['approve'],
};
const ACTION_LABELS = { approve: 'Approve', request_info: 'Request info', reject: 'Reject', suspend: 'Suspend', reinstate: 'Reinstate' };
const NEEDS_REASON = ['reject', 'suspend', 'request_info'];
const fmt = (d) => (d ? new Date(d).toLocaleString() : '—');
const errMsg = (e) => { const d = e.response?.data?.detail; return typeof d === 'string' ? d : 'Something went wrong'; };

const Row = ({ label, value }) => (value ? <p><span className="text-gray-500">{label}:</span> {String(value)}</p> : null);

const Credentials = ({ p }) => (
  <div className="text-sm space-y-1 bg-gray-50 rounded-lg p-3" data-testid="review-credentials">
    <Row label="Business" value={p.company} /><Row label="Category" value={p.professional_type} />
    <Row label="Jurisdiction" value={p.jurisdiction} /><Row label="Licence #" value={p.license_number} />
    <Row label="Licensing body" value={p.licensing_body} /><Row label="Email" value={p.email} />
    <Row label="Business email" value={p.business_email} /><Row label="Phone" value={p.phone} />
    <Row label="Website" value={p.website} /><Row label="Certifications" value={(p.certifications || []).join(', ')} />
    <Row label="Fee disclosure" value={p.fee_disclosure} /><Row label="Conflicts of interest" value={p.conflicts_of_interest} />
    <Row label="Standards accepted" value={p.standards_accepted_at ? fmt(p.standards_accepted_at) : 'Not yet'} />
    {p.family_endorsed && <p className="text-indigo-700">Recommended by an LFF family</p>}
  </div>
);

export const ProReviewDialog = ({ profileId, onClose, onChanged }) => {
  const [d, setD] = useState(null);
  const [reason, setReason] = useState('');
  const [verifyNotes, setVerifyNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => axios.get(`/api/directory/admin/professionals/${profileId}/detail`).then(r => setD(r.data)), [profileId]);
  useEffect(() => { load(); }, [load]);

  const run = async (fn, ok) => {
    setBusy(true);
    try { await fn(); toast.success(ok); setReason(''); await load(); onChanged(); }
    catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };
  const decide = (action) => {
    if (NEEDS_REASON.includes(action) && !reason.trim()) return toast.error('Please write a reason first');
    run(() => axios.post(`/api/directory/admin/professionals/${profileId}/decision`, { action, reason: reason || null }), `${ACTION_LABELS[action]}: done`);
  };
  const verify = (v) => run(() => axios.patch(`/api/directory/admin/professionals/${profileId}/verification`, { credentials_verified: v, verification_notes: verifyNotes || null }), 'Verification saved');
  const resolve = (id, outcome) => run(() => axios.post(`/api/directory/admin/complaints/${id}/resolve`, { outcome, notes: reason || null }), `Complaint ${outcome}`);

  const p = d?.profile;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="pro-review-dialog">
        <DialogHeader><DialogTitle>{p ? `${p.name} — ${STATUS_LABELS[p.status] || p.status}` : 'Loading…'}</DialogTitle></DialogHeader>
        {p && (
          <div className="space-y-5">
            <Credentials p={p} />
            <section>
              <h4 className="font-semibold mb-2">Credential verification</h4>
              <p className="text-sm text-gray-600 mb-2">{p.credentials_verified ? 'Verified' : 'Not verified'}{p.verified_at ? ` on ${fmt(p.verified_at)} by ${p.verified_by_name}` : ''}</p>
              <input value={verifyNotes} onChange={e => setVerifyNotes(e.target.value)} placeholder="How you verified (e.g., checked State Bar website)" className="w-full px-3 py-2 border rounded-lg text-sm mb-2" data-testid="verification-notes-input" />
              <div className="flex gap-2">
                <button disabled={busy} onClick={() => verify(true)} className="px-3 py-1.5 bg-green-600 text-white rounded-lg text-sm" data-testid="mark-verified-btn">Mark credentials verified</button>
                <button disabled={busy} onClick={() => verify(false)} className="px-3 py-1.5 border rounded-lg text-sm" data-testid="mark-unverified-btn">Mark not verified</button>
              </div>
            </section>
            <section>
              <h4 className="font-semibold mb-2">Decision</h4>
              <textarea value={reason} onChange={e => setReason(e.target.value)} rows={2} placeholder="Reason / message to the professional (required for reject, suspend, request info; used as notes when resolving complaints)" className="w-full px-3 py-2 border rounded-lg text-sm mb-2" data-testid="decision-reason-input" />
              <div className="flex flex-wrap gap-2">
                {(ACTIONS[p.status] || []).map(a => (
                  <button key={a} disabled={busy} onClick={() => decide(a)} data-testid={`decision-${a}-btn`}
                    className={`px-3 py-1.5 rounded-lg text-sm text-white ${a === 'approve' || a === 'reinstate' ? 'bg-green-600' : a === 'request_info' ? 'bg-amber-600' : 'bg-red-600'}`}>{ACTION_LABELS[a]}</button>
                ))}
              </div>
            </section>
            <section>
              <h4 className="font-semibold mb-2">Complaints ({d.complaints.length})</h4>
              {d.complaints.length === 0 && <p className="text-sm text-gray-500">None</p>}
              {d.complaints.map(c => (
                <div key={c.complaint_id} className="border rounded-lg p-3 mb-2 text-sm" data-testid={`complaint-${c.complaint_id}`}>
                  <p className="font-medium">{c.category.replace(/_/g, ' ')} · {c.status} · {fmt(c.created_at)}</p>
                  <p className="text-gray-700 mt-1">{c.details}</p>
                  <p className="text-xs text-gray-500 mt-1">From {c.reporter_name} ({c.reporter_email})</p>
                  {c.status === 'open' && (
                    <div className="flex gap-2 mt-2">
                      <button disabled={busy} onClick={() => resolve(c.complaint_id, 'dismissed')} className="px-3 py-1 border rounded text-xs" data-testid={`dismiss-complaint-${c.complaint_id}`}>Dismiss</button>
                      <button disabled={busy} onClick={() => resolve(c.complaint_id, 'upheld')} className="px-3 py-1 bg-red-600 text-white rounded text-xs" data-testid={`uphold-complaint-${c.complaint_id}`}>Uphold</button>
                    </div>
                  )}
                </div>
              ))}
            </section>
            <section>
              <h4 className="font-semibold mb-2">Decision history</h4>
              <ul className="text-sm space-y-1" data-testid="decision-history">
                {d.history.length === 0 && <li className="text-gray-500">No decisions yet</li>}
                {d.history.map(h => (
                  <li key={h.decision_id} className="border-l-2 border-amber-400 pl-2">
                    <span className="font-medium">{h.action.replace(/_/g, ' ')}</span> by {h.decided_by_name} · {fmt(h.decided_at)}
                    {h.from_status !== h.to_status && <span className="text-gray-500"> ({h.from_status} → {h.to_status})</span>}
                    {h.reason && <span className="block text-gray-600">{h.reason}</span>}
                  </li>
                ))}
              </ul>
            </section>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
